"use client";

import DynamicIcon from "@/components/atoms/DynamicIcon";
import { ctaClick } from "@/customAnalytics/trackingEvents";
import { cn } from "@/lib/utils";
import { handleOpenLeaddrop } from "@/store/slices/leaddrop";
import { isCmsShowFlagOn } from "@/utils/variantHelpers";
import Image from "next/image";
import Link from "next/link";
import { useCallback, useMemo } from "react";
import { useDispatch } from "react-redux";
import { LEAD_DROP_VARIANTS } from "../../../constants";

/**
 * Banner 1059 — KUK home referral band.
 *
 * Port of kuk-campus-portal `components/Referral/ReferralPreLogin.tsx`: a piece
 * of full-bleed artwork with a bold headline over it, one line of supporting
 * copy and a small gradient pill CTA that opens the sign-up form.
 *
 * BREAKPOINTS — the v1 source is built on Tailwind's DEFAULT scale, where `md:`
 * is 768px and `lg:` is 1024px. This app remaps `md:` to 1230px and exposes
 * 768px as `tab:` (see globals.css `--breakpoint-*`), so the reference's `md:`
 * is ported as `tab:` and its `lg:` as this app's `sm:` (1080px). The artwork
 * swap in v1 is on `lg:`, so it happens at `sm:` here; the padding changes are
 * on `md:`, so they happen at `tab:`.
 *
 * THE HIGHLIGHT — v1 passes the headline through a helper that turns `{` and `}`
 * into a `<span>` with a highlight class, so "earn {50%} of your course fee"
 * paints a chip behind "50%". Reproduced with the same brace syntax, with the
 * chip colour as a CMS field rather than a fixed class. The headline is escaped
 * before the braces are expanded, so stored markup cannot inject anything.
 *
 * BACKGROUND ARTWORK — v1 uses `next/image` with `fill` for both the mobile and
 * desktop artwork, keeping them as real images (so they get optimised and sized)
 * rather than CSS backgrounds. That is kept.
 */

interface Banner1059Data {
  /** Artwork from 1080px up. */
  background_image?: string;
  /** Artwork below 1080px; falls back to the desktop artwork. */
  background_image_mobile?: string;
  image_alt?: string;
  /** Headline. `{...}` wraps a highlighted chip. */
  title?: string;
  title_color?: string;
  /** The chip behind a `{...}` span. */
  title_highlight_color?: string;
  description?: string;
  description_color?: string;
  apply_now_show?: boolean;
  apply_now_text?: string;
  apply_now_link?: string;
  apply_now_is_cta?: boolean;
  apply_now_color?: string;
  /** CTA pill gradient. */
  gradient_color_1?: string;
  gradient_color_2?: string;
  gradient_color_3?: string;
  /** Icon-map key or an uploaded image URL, shown inside the pill. */
  icon?: string;
  alt_icon?: string;
  status?: boolean;
}

const ESCAPE: Record<string, string> = {
  "&": "&amp;",
  "<": "&lt;",
  ">": "&gt;",
  '"': "&quot;",
  "'": "&#39;",
};

/**
 * v1's `textStyle`: `{` opens a highlighted span, `}` closes it. The headline is
 * escaped first, so only the braces this widget understands become markup.
 */
function highlightBraces(title: string, color: string): string {
  if (!title) return "";
  return title
    .replace(/[&<>"']/g, (c) => ESCAPE[c])
    .replace(
      /\{/g,
      `<span class="whitespace-nowrap" style="background-color:${color}">`,
    )
    .replace(/\}/g, "</span>");
}

function normalizeHref(href: string): string {
  const h = href.trim();
  if (!h) return "#";
  if (
    h.startsWith("http://") ||
    h.startsWith("https://") ||
    h.startsWith("/") ||
    h.startsWith("#") ||
    h.startsWith("mailto:") ||
    h.startsWith("tel:")
  ) {
    return h;
  }
  return `/${h.replace(/^\//, "")}`;
}

const Banner1059 = (props: { data?: Banner1059Data }) => {
  const { data: raw } = props;
  const sectionData = ((raw as any)?.data ?? raw) as Banner1059Data | undefined;
  const dispatch = useDispatch();

  const {
    background_image = "",
    background_image_mobile = "",
    image_alt = "",
    title = "",
    title_color = "#FFFFFF",
    title_highlight_color = "#E26C24",
    description = "",
    description_color = "#FFFFFF",
    apply_now_text = "Start referring Now!",
    apply_now_link = "",
    apply_now_color = "#FFFFFF",
    gradient_color_1 = "#B0325E",
    gradient_color_2 = "#FAA681",
    gradient_color_3 = "47deg",
    icon = "",
    alt_icon = "",
  } = sectionData || {};

  const applyNowShow = isCmsShowFlagOn(sectionData?.apply_now_show, true);
  const applyNowIsCta = isCmsShowFlagOn(sectionData?.apply_now_is_cta, true);
  const status = isCmsShowFlagOn(sectionData?.status, true);

  const headline = useMemo(
    () => highlightBraces(title, title_highlight_color),
    [title, title_highlight_color],
  );

  const handleApplyNowClick = useCallback(() => {
    ctaClick({
      section: "banner_1059",
      component: "referral_cta",
      label: apply_now_text?.trim() || "start_referring",
      step: "1",
      entity: "banner_1059",
      parentEntity: "banner_1059",
    });
    dispatch(handleOpenLeaddrop({ variant: LEAD_DROP_VARIANTS.REGISTER }));
  }, [apply_now_text, dispatch]);

  if (!status) return null;

  const desktopArt = background_image || background_image_mobile;
  const mobileArt = background_image_mobile || background_image;

  const ctaClassName = cn(
    "flex items-center gap-1 self-start rounded-3xl px-4 py-1",
    "text-xs font-semibold",
  );
  const ctaStyle = {
    backgroundImage: `linear-gradient(${gradient_color_3}, ${gradient_color_1} 3.7%, ${gradient_color_2} 106.2%)`,
    color: apply_now_color,
  };

  return (
    <section
      className={cn(
        "relative w-full px-6 pb-[108px] pt-10",
        "tab:py-10 tab:pl-[92px]",
      )}
    >
      <div className="absolute inset-0 z-0 size-full">
        {mobileArt && (
          <Image
            src={mobileArt}
            alt={image_alt || ""}
            fill
            loading="lazy"
            sizes="100vw"
            className="block object-cover sm:hidden"
          />
        )}
        {desktopArt && (
          <Image
            src={desktopArt}
            alt={image_alt || ""}
            fill
            loading="lazy"
            sizes="100vw"
            className="hidden object-cover sm:block"
          />
        )}
      </div>

      <div className="relative z-10 mx-auto flex flex-col gap-5">
        <div className="flex w-[255px] flex-col gap-2 tab:w-full tab:max-w-[816px]">
          {title && (
            <p
              className="text-2xl font-bold tab:text-[32px]"
              style={{ color: title_color }}
              dangerouslySetInnerHTML={{ __html: headline }}
            />
          )}
          {description && (
            <p
              className="text-xs font-medium"
              style={{ color: description_color }}
            >
              {description}
            </p>
          )}
        </div>

        {applyNowShow &&
          apply_now_text &&
          (applyNowIsCta || !apply_now_link ? (
            <button
              type="button"
              onClick={handleApplyNowClick}
              className={ctaClassName}
              style={ctaStyle}
            >
              {icon && (
                <span
                  className="[&_img]:size-6 [&_svg]:size-6"
                  aria-label={alt_icon || undefined}
                >
                  <DynamicIcon
                    iconName={icon}
                    color={apply_now_color}
                    width="24"
                    height="24"
                  />
                </span>
              )}
              {apply_now_text}
            </button>
          ) : (
            <Link
              href={normalizeHref(apply_now_link)}
              className={ctaClassName}
              style={ctaStyle}
            >
              {icon && (
                <span
                  className="[&_img]:size-6 [&_svg]:size-6"
                  aria-label={alt_icon || undefined}
                >
                  <DynamicIcon
                    iconName={icon}
                    color={apply_now_color}
                    width="24"
                    height="24"
                  />
                </span>
              )}
              {apply_now_text}
            </Link>
          ))}
      </div>
    </section>
  );
};

export default Banner1059;
